/**
 * PortlessManifestSettingsSection — configure who receives the daily Portless
 * inbound packing-list manifest, and view the packing-list template.
 *
 * Renders inside Settings → Advanced → Portless, ABOVE the `PortlessManifestCard`
 * (the fire-once "Run now" action). Persisted config (To + CC) with a dirty-state
 * Save; the From address is READ-ONLY here because it reuses the organization's
 * existing (Resend-verified) sender — change it in Settings → Purchase Orders.
 *
 * To/CC edits are admin-only + audited on the backend (updateManifestSettings).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Eye, FileSpreadsheet, Loader2, Plus, RotateCcw, Save, Warehouse, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  PORTLESS_PLACEHOLDER_TOKENS,
  renderManifestHtml,
  renderManifestText,
} from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useRole } from '@/hooks/use-role';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_CC = 10;
const MAX_SUBJECT = 200;
const MAX_BODY = 2000;
/**
 * Sample values for the live preview — mirror `buildSummary` in the backend send
 * path (service.ts). Uses the SPLIT form so an admin editing the body can see
 * what a Hybrid run actually reads like.
 */
const PREVIEW_SUMMARY =
  '3 order(s), 12 SKU line(s): 9 inbound to Portless from ThreadLab, 3 to be picked from Portless stock.';
const PREVIEW_TYPE: Record<'crossdock' | 'hybrid', string> = {
  crossdock: 'Cross Dock',
  hybrid: 'Hybrid',
};
/** Any `{token}` not in the allowlist won't be substituted — warn (non-blocking). */
const UNKNOWN_TOKEN_RE = /\{([a-zA-Z0-9_]+)\}/g;
const findUnknownTokens = (text: string): string[] => {
  const known = new Set<string>(PORTLESS_PLACEHOLDER_TOKENS);
  const seen = new Set<string>();
  for (const m of text.matchAll(UNKNOWN_TOKEN_RE)) {
    const tok = m[1];
    if (tok && !known.has(tok)) seen.add(tok);
  }
  return [...seen];
};

/**
 * Mirrors HEADERS in `modules/portless-export/xlsx.ts`, in order. `required` =
 * bold in Portless's own template ("Bold columns are required info"), so our
 * informational additions are NOT required.
 */
const TEMPLATE_COLUMNS = [
  { key: 'sno', required: false },
  { key: 'shopifyOrder', required: true },
  { key: 'portlessOrder', required: true },
  { key: 'sku', required: true },
  { key: 'itemDescription', required: true },
  { key: 'name', required: true },
  { key: 'surname', required: true },
  { key: 'units', required: true },
  { key: 'fulfilledFrom', required: false },
] as const;

/** Trigger a browser download from a base64-encoded .xlsx payload. */
function downloadBase64Xlsx(base64: string, filename: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function PortlessManifestSettingsSection() {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  // Recipient edits re-point where a customer-PII manifest is emailed, so the
  // backend gates updateManifestSettings on adminProcedure. Mirror that here so
  // coordinators get a read-only view instead of editable fields that 403 on save.
  const { isAdmin } = useRole();
  const canEdit = isAdmin();

  const [toEmail, setToEmail] = useState('');
  const [cc, setCc] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState('');
  const [ccError, setCcError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [downloading, setDownloading] = useState(false);
  /**
   * In-flight stock-location choice, keyed by manifest location. The Select is
   * otherwise controlled straight off server data, so between the click and the
   * response it would keep rendering the OLD floor — which reads as "my click
   * didn't take" and invites a second click. Cleared in onSettled, after
   * onSuccess has patched the cache, so the value never flashes back.
   */
  const [pendingStockLocation, setPendingStockLocation] = useState<Record<string, string>>({});

  // Editable email content (BLU-600). Blank = use the built-in default; the
  // default shows as placeholder and a blank save persists null.
  const [subjectTemplate, setSubjectTemplate] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [previewType, setPreviewType] = useState<'crossdock' | 'hybrid'>('crossdock');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastFocusedRef = useRef<'subject' | 'body'>('body');

  const settingsQuery = trpc.portlessExport.getManifestSettings.useQuery();

  const updateMutation = trpc.portlessExport.updateManifestSettings.useMutation({
    onSuccess: () => {
      toast.success(t('production.portlessManifest.saved'));
      setHasChanges(false);
      void settingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`${t('production.portlessManifest.saveFailed')}: ${error.message}`, {
        duration: Infinity,
      });
    },
  });

  // Applied immediately (no dirty-state Save), mirroring the per-location Select
  // in ShippingProviderSettingsSection: it is one discrete choice, not part of
  // the email-content form, and pairing it with the Save button would imply the
  // two are saved together.
  const setStockLocationMutation = trpc.portlessExport.setStockLocation.useMutation({
    onSuccess: (res) => {
      toast.success(t('production.portlessManifest.stockLocation.saved'));
      // Patch the cache instead of refetching. A refetch returns a payload with a
      // changed identity, which is what the hydration effect below is keyed on —
      // that effect then overwrites the subject/body/To/CC edit fields from
      // stored values AND clears hasChanges, silently discarding an in-progress
      // email draft along with the cue that would have revealed it. This setting
      // is independent of the email form, so the write must not touch it.
      // Only the row that was written is patched; every other manifest floor's
      // designation is left byte-identical.
      utils.portlessExport.getManifestSettings.setData(undefined, (prev) =>
        prev
          ? {
              ...prev,
              manifestLocations: prev.manifestLocations.map((m) =>
                m.id === res.manifestLocationId ? { ...m, stockLocationId: res.stockLocationId } : m,
              ),
            }
          : prev,
      );
    },
    onError: (error) => {
      // Resolved into a local before the template literal, not inlined into it:
      // scripts/i18n-check.mjs scans string literals in a single regex pass, and a
      // backtick literal is consumed whole — so a t('key') nested inside one is
      // invisible to it and the key gets reported UNREFERENCED.
      const failed = t('production.portlessManifest.stockLocation.saveFailed');
      toast.error(`${failed}: ${error.message}`, {
        duration: Infinity,
      });
    },
    // Settles both ways: on success the cache has already been patched above, so
    // dropping the pending value lands on the same value; on failure it reverts
    // the trigger to the server value, which is the truth after a rejected write.
    onSettled: (_res, _err, variables) => {
      setPendingStockLocation((prev) => {
        if (!(variables.manifestLocationId in prev)) return prev;
        const next = { ...prev };
        delete next[variables.manifestLocationId];
        return next;
      });
    },
  });

  useEffect(() => {
    if (settingsQuery.data) {
      // Bind edit fields to the RAW stored DB values (blank when the effective
      // recipient is env-sourced). Binding to the resolved values would pin an
      // env-sourced address into org settings on the next save and shadow any
      // later env flip. The effective/env value is shown read-only below.
      setToEmail(settingsQuery.data.storedToEmail ?? '');
      setCc(settingsQuery.data.storedCc ?? []);
      setSubjectTemplate(settingsQuery.data.storedSubjectTemplate ?? '');
      setBodyTemplate(settingsQuery.data.storedBodyTemplate ?? '');
      setHasChanges(false);
      setCcInput('');
      setCcError(null);
    }
  }, [settingsQuery.data]);

  /** Radix Select cannot hold an empty-string value, so "not designated" needs a sentinel. */
  const NO_STOCK_LOCATION = '__none__';
  /** The floors that send a Portless manifest — one designation row each. */
  const manifestLocations = settingsQuery.data?.manifestLocations ?? [];
  /** The org's own locations — the candidate stock floors. */
  const orgLocations = settingsQuery.data?.locations ?? [];
  const manifestLocationIds = new Set(manifestLocations.map((l) => l.id));
  /**
   * Candidates for one row. A manifest floor can never also be a stock floor —
   * the backend rejects it (it would self-join the export and drop that floor's
   * own legs off its own sheet), so it must not be offerable in the first place.
   *
   * The row's OWN current designation stays in the list even if it has since
   * become a manifest floor, for the same reason the backend keeps inactive
   * locations in the candidate list: a value the Select cannot render is a
   * designation the operator can neither see nor clear, while it still drives
   * the export.
   */
  const stockLocationOptions = (current: string | null) =>
    orgLocations.filter((l) => !manifestLocationIds.has(l.id) || l.id === current);

  const defaultSubjectTemplate = settingsQuery.data?.defaultSubjectTemplate ?? '';
  const defaultBodyTemplate = settingsQuery.data?.defaultBodyTemplate ?? '';
  // Effective template = the override if present, else the default — this is
  // what actually sends, so the preview renders it (never a blank box).
  const effectiveSubject = subjectTemplate.trim().length > 0 ? subjectTemplate : defaultSubjectTemplate;
  const effectiveBody = bodyTemplate.trim().length > 0 ? bodyTemplate : defaultBodyTemplate;

  const previewVars = useMemo(
    () => ({ type: PREVIEW_TYPE[previewType], date: new Date().toISOString().slice(0, 10), summary: PREVIEW_SUMMARY }),
    [previewType],
  );
  const previewSubject = useMemo(
    () => renderManifestText(effectiveSubject, previewVars),
    [effectiveSubject, previewVars],
  );
  const previewHtml = useMemo(() => renderManifestHtml(effectiveBody, previewVars), [effectiveBody, previewVars]);

  const subjectTooLong = subjectTemplate.length > MAX_SUBJECT;
  const bodyTooLong = bodyTemplate.length > MAX_BODY;
  const subjectHasLineBreak = /[\r\n\t]/.test(subjectTemplate);
  const unknownTokens = useMemo(
    () => findUnknownTokens(`${subjectTemplate}\n${bodyTemplate}`),
    [subjectTemplate, bodyTemplate],
  );

  /** Insert a placeholder token at the caret of the last-focused field. */
  const insertToken = (token: string) => {
    const wrapped = `{${token}}`;
    if (lastFocusedRef.current === 'subject') {
      const el = subjectRef.current;
      const start = el?.selectionStart ?? subjectTemplate.length;
      const end = el?.selectionEnd ?? subjectTemplate.length;
      const next = subjectTemplate.slice(0, start) + wrapped + subjectTemplate.slice(end);
      setSubjectTemplate(next);
      setHasChanges(true);
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(start + wrapped.length, start + wrapped.length);
      });
    } else {
      const el = bodyRef.current;
      const start = el?.selectionStart ?? bodyTemplate.length;
      const end = el?.selectionEnd ?? bodyTemplate.length;
      const next = bodyTemplate.slice(0, start) + wrapped + bodyTemplate.slice(end);
      setBodyTemplate(next);
      setHasChanges(true);
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(start + wrapped.length, start + wrapped.length);
      });
    }
  };

  const handleResetContent = () => {
    setSubjectTemplate('');
    setBodyTemplate('');
    setHasChanges(true);
  };

  const toInvalid = toEmail.trim().length > 0 && !EMAIL_RE.test(toEmail.trim());

  const handleAddCc = () => {
    const value = ccInput.trim();
    if (!value) return;
    if (!EMAIL_RE.test(value)) {
      setCcError(t('production.portlessManifest.invalidEmail'));
      return;
    }
    if (cc.includes(value)) {
      setCcInput('');
      return;
    }
    if (cc.length >= MAX_CC) {
      setCcError(t('production.portlessManifest.ccMax', { count: MAX_CC }));
      return;
    }
    setCc((prev) => [...prev, value]);
    setCcInput('');
    setCcError(null);
    setHasChanges(true);
  };

  const handleRemoveCc = (email: string) => {
    setCc((prev) => prev.filter((c) => c !== email));
    setHasChanges(true);
  };

  const handleSave = () => {
    const trimmed = toEmail.trim();
    const subj = subjectTemplate.trim();
    const body = bodyTemplate.trim();
    updateMutation.mutate({
      toEmail: trimmed.length > 0 ? trimmed : null,
      cc,
      // Blank field → null → backend falls back to the built-in default.
      subjectTemplate: subj.length > 0 ? subj : null,
      bodyTemplate: body.length > 0 ? body : null,
    });
  };

  const handleDownload = async (which: 'crossdock' | 'hybrid') => {
    setDownloading(true);
    try {
      const samples = await utils.portlessExport.getTemplateSample.fetch();
      const base64 = which === 'crossdock' ? samples.crossdock : samples.hybrid;
      downloadBase64Xlsx(
        base64,
        which === 'crossdock' ? 'Portless-CrossDock-template.xlsx' : 'Portless-Hybrid-template.xlsx',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  const fromEmail = settingsQuery.data?.fromEmail ?? '';
  const contentInvalid = subjectTooLong || bodyTooLong || subjectHasLineBreak;
  const hasContentOverride = subjectTemplate.trim().length > 0 || bodyTemplate.trim().length > 0;
  const saveDisabled = !canEdit || updateMutation.isPending || !hasChanges || toInvalid || contentInvalid;

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <div>
        <h4 className="font-medium">{t('production.portlessManifest.title')}</h4>
        <p className="text-sm text-muted-foreground">
          {t('production.portlessManifest.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="portless-to" className="text-sm text-muted-foreground">
            {t('production.portlessManifest.toEmail')}
            <span className="text-destructive ml-1" aria-hidden="true">*</span>
          </Label>
          <Input
            id="portless-to"
            type="email"
            value={toEmail}
            onChange={(e) => {
              setToEmail(e.target.value);
              setHasChanges(true);
            }}
            placeholder="ops@portless.com"
            className="h-11 max-w-prose"
            aria-invalid={toInvalid}
            readOnly={!canEdit}
            disabled={!canEdit}
          />
          {toInvalid ? (
            <p className="text-sm text-destructive">{t('production.portlessManifest.invalidEmail')}</p>
          ) : (
            <p className="text-xs text-muted-foreground">{t('production.portlessManifest.toEmailHelp')}</p>
          )}
          {settingsQuery.data?.source.to === 'env' && (
            <p className="text-xs text-muted-foreground">
              {t('production.portlessManifest.envDefaultTo', { email: settingsQuery.data.toEmail })}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="portless-from" className="text-sm text-muted-foreground">
            {t('production.portlessManifest.fromEmail')}
          </Label>
          <Input
            id="portless-from"
            type="email"
            value={fromEmail}
            readOnly
            disabled
            className="h-11 max-w-prose"
          />
          <p className="text-xs text-muted-foreground">{t('production.portlessManifest.fromEmailHelp')}</p>
        </div>
      </div>

      {/* CC list */}
      <div className="space-y-2">
        <Label htmlFor="portless-cc" className="text-sm text-muted-foreground">
          {t('production.portlessManifest.ccLabel')}
        </Label>
        {cc.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {cc.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1 rounded-full border border-threadheads-purple/30 bg-threadheads-purple/10 px-3 py-1 text-sm text-threadheads-purple"
              >
                {email}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemoveCc(email)}
                    aria-label={t('production.portlessManifest.ccRemove', { email })}
                    className="inline-flex min-h-[32px] min-w-[32px] items-center justify-center rounded-full hover:bg-threadheads-purple/20"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input
            id="portless-cc"
            type="email"
            value={ccInput}
            onChange={(e) => {
              setCcInput(e.target.value);
              setCcError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddCc();
              }
            }}
            placeholder="warehouse@threadheads.com"
            className="h-11 max-w-prose"
            aria-invalid={!!ccError}
            disabled={!canEdit}
          />
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={handleAddCc}
            disabled={!canEdit || !ccInput.trim() || cc.length >= MAX_CC}
          >
            <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
            {t('production.portlessManifest.ccAdd')}
          </Button>
        </div>
        {ccError ? (
          <p className="text-sm text-destructive">{ccError}</p>
        ) : (
          <p className="text-xs text-muted-foreground">{t('production.portlessManifest.ccHelp')}</p>
        )}
      </div>

      {/* Email content (editable subject + body) */}
      <div className="space-y-3 border-t pt-4 mt-4">
        <div>
          <h4 className="font-medium">{t('production.portlessManifest.emailContent.title')}</h4>
          <p className="text-sm text-muted-foreground">
            {t('production.portlessManifest.emailContent.description')}
          </p>
        </div>

        {/* Placeholder legend — click to insert */}
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">
            {t('production.portlessManifest.emailContent.placeholderCaption')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PORTLESS_PLACEHOLDER_TOKENS.map((token) => (
              <button
                key={token}
                type="button"
                disabled={!canEdit}
                onClick={() => insertToken(token)}
                aria-label={t('production.portlessManifest.emailContent.insertPlaceholder', { token: `{${token}}` })}
                className="rounded border px-2 py-1 text-xs transition-colors duration-200 hover:border-threadheads-purple/40 hover:text-threadheads-purple disabled:opacity-50 motion-reduce:transition-none"
              >
                <span className="font-mono">{`{${token}}`}</span>
                <span className="ml-1 text-muted-foreground">
                  {t(`production.portlessManifest.emailContent.token.${token}`)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div className="space-y-1">
          <Label htmlFor="portless-subject" className="text-sm text-muted-foreground">
            {t('production.portlessManifest.emailContent.subjectLabel')}
          </Label>
          <Input
            id="portless-subject"
            ref={subjectRef}
            value={subjectTemplate}
            onFocus={() => (lastFocusedRef.current = 'subject')}
            onChange={(e) => {
              setSubjectTemplate(e.target.value);
              setHasChanges(true);
            }}
            placeholder={defaultSubjectTemplate}
            className="h-11 max-w-prose"
            maxLength={MAX_SUBJECT + 50}
            aria-invalid={subjectTooLong || subjectHasLineBreak}
            aria-describedby={
              subjectHasLineBreak || subjectTooLong
                ? 'portless-subject-error portless-subject-counter'
                : 'portless-subject-counter'
            }
            readOnly={!canEdit}
            disabled={!canEdit}
          />
          <div className="flex items-center justify-between gap-2">
            {subjectHasLineBreak ? (
              <p id="portless-subject-error" className="text-sm text-destructive">
                {t('production.portlessManifest.emailContent.subjectNoLineBreaks')}
              </p>
            ) : subjectTooLong ? (
              <p id="portless-subject-error" className="text-sm text-destructive">
                {t('production.portlessManifest.emailContent.subjectTooLong', { max: MAX_SUBJECT })}
              </p>
            ) : subjectTemplate.trim().length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('production.portlessManifest.emailContent.usingDefault')}
              </p>
            ) : (
              <span aria-hidden="true" />
            )}
            <span
              id="portless-subject-counter"
              className={`text-xs tabular-nums ${subjectTemplate.length >= MAX_SUBJECT * 0.95 ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {subjectTemplate.length}/{MAX_SUBJECT}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="space-y-1">
          <Label htmlFor="portless-body" className="text-sm text-muted-foreground">
            {t('production.portlessManifest.emailContent.bodyLabel')}
          </Label>
          <Textarea
            id="portless-body"
            ref={bodyRef}
            value={bodyTemplate}
            rows={6}
            onFocus={() => (lastFocusedRef.current = 'body')}
            onChange={(e) => {
              setBodyTemplate(e.target.value);
              setHasChanges(true);
            }}
            placeholder={defaultBodyTemplate}
            className="text-sm max-w-prose"
            maxLength={MAX_BODY + 200}
            aria-invalid={bodyTooLong}
            aria-describedby={
              bodyTooLong ? 'portless-body-error portless-body-counter' : 'portless-body-counter'
            }
            readOnly={!canEdit}
            disabled={!canEdit}
          />
          <div className="flex items-center justify-between gap-2">
            {bodyTooLong ? (
              <p id="portless-body-error" className="text-sm text-destructive">
                {t('production.portlessManifest.emailContent.bodyTooLong', { max: MAX_BODY })}
              </p>
            ) : bodyTemplate.trim().length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('production.portlessManifest.emailContent.usingDefault')}
              </p>
            ) : (
              <span aria-hidden="true" />
            )}
            <span
              id="portless-body-counter"
              className={`text-xs tabular-nums ${bodyTemplate.length >= MAX_BODY * 0.95 ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {bodyTemplate.length}/{MAX_BODY}
            </span>
          </div>
        </div>

        {unknownTokens.length > 0 && (
          <p className="text-xs text-amber-700" role="status">
            {t('production.portlessManifest.emailContent.unknownToken', {
              tokens: unknownTokens.map((tok) => `{${tok}}`).join(', '),
            })}
          </p>
        )}

        {canEdit && hasContentOverride && (
          <Button type="button" variant="ghost" size="sm" onClick={handleResetContent} className="gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            {t('production.portlessManifest.emailContent.reset')}
          </Button>
        )}

        {/* Live preview */}
        <div
          role="group"
          className="rounded-md border bg-muted/40 p-3 space-y-2"
          aria-label={t('production.portlessManifest.emailContent.previewTitle')}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm font-medium">{t('production.portlessManifest.emailContent.previewTitle')}</span>
            </div>
            <div className="inline-flex rounded-md border p-0.5">
              {(['crossdock', 'hybrid'] as const).map((which) => (
                <button
                  key={which}
                  type="button"
                  onClick={() => setPreviewType(which)}
                  aria-pressed={previewType === which}
                  className={`rounded px-2 py-1 text-xs transition-colors duration-200 motion-reduce:transition-none ${
                    previewType === which
                      ? 'bg-threadheads-purple text-white'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t(`production.portlessManifest.emailContent.preview.${which}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <span className="text-xs text-muted-foreground">
                {t('production.portlessManifest.emailContent.previewSubject')}
              </span>
              <p className="text-sm font-medium break-words">{previewSubject}</p>
            </div>
            <div className="rounded bg-background p-3 text-sm [&_p]:mb-2 [&_p:last-child]:mb-0">
              {/* Body is stored/edited as plain text and HTML-escaped by renderManifestHtml,
                  so the interpolated markup is inert (escaped) — safe to render. */}
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        </div>
      </div>

      {/* Portless-held stock location — drives the Fulfilled From column, so it
          sits directly above the template preview that shows that column.

          Scoped PER MANIFEST FLOOR. An org can send more than one Portless
          manifest at a time (Threadheads sends two today: its own Thread Lab
          floor and Ningbo, reached through a shared-location junction), and a
          single org-wide designation is claimed by every one of them — the same
          Portless-held units then go out on every sheet and Portless is told to
          pick the same physical garments more than once. One row per sending
          floor makes that impossible. */}
      <div className="rounded-md border bg-muted/40 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">
            {t('production.portlessManifest.stockLocation.title')}
          </span>
        </div>
        <p id="portless-stock-desc" className="max-w-prose text-xs text-muted-foreground">
          {t('production.portlessManifest.stockLocation.description')}
        </p>

        {manifestLocations.length === 0 ? (
          // Neither an error nor a warning: an org with no Portless floor has
          // nothing to designate. An empty row list would read as a failed load.
          <p className="max-w-prose text-xs text-muted-foreground">
            {t('production.portlessManifest.stockLocation.noManifestLocations')}
          </p>
        ) : (
          <div
            className="space-y-2 pt-1"
            role="list"
            aria-label={t('production.portlessManifest.stockLocation.title')}
          >
            {manifestLocations.map((manifestLocation) => {
              const displayName = manifestLocation.isActive
                ? manifestLocation.name
                : t('production.portlessManifest.stockLocation.inactiveOption', {
                    name: manifestLocation.name,
                  });
              const pendingValue = pendingStockLocation[manifestLocation.id];
              const rowPending = pendingValue !== undefined;
              const value = pendingValue ?? manifestLocation.stockLocationId ?? NO_STOCK_LOCATION;
              const isUnset = value === NO_STOCK_LOCATION;
              // Ids are per row: a single shared id would make every trigger
              // describe the first row's warning.
              const warningId = `portless-stock-warning-${manifestLocation.id}`;
              return (
                <div key={manifestLocation.id} role="listitem" className="rounded-md bg-background/70 p-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-xs font-medium">{displayName}</span>
                    <div className="flex items-center gap-2">
                      {rowPending && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
                      )}
                      <Select
                        value={value}
                        onValueChange={(v) => {
                          setPendingStockLocation((prev) => ({ ...prev, [manifestLocation.id]: v }));
                          setStockLocationMutation.mutate({
                            manifestLocationId: manifestLocation.id,
                            stockLocationId: v === NO_STOCK_LOCATION ? null : v,
                          });
                        }}
                        disabled={!canEdit}
                      >
                        <SelectTrigger
                          className="h-8 w-[240px] text-xs"
                          aria-label={t('production.portlessManifest.stockLocation.rowLabel', {
                            name: displayName,
                          })}
                          // Both the explanation and the consequence are wired up,
                          // so a screen-reader user hears why this control matters,
                          // not just its name — but only while the warning exists:
                          // an idref pointing at an absent element is silently
                          // dropped, so the description is built conditionally
                          // (same shape as the subject/body fields above).
                          // aria-busy rather than `disabled` while the write is in
                          // flight: disabling the focused trigger drops focus to the
                          // body and restarts Tab order from the top of the document.
                          aria-describedby={
                            isUnset ? `portless-stock-desc ${warningId}` : 'portless-stock-desc'
                          }
                          aria-busy={rowPending}
                        >
                          <SelectValue placeholder={t('production.portlessManifest.stockLocation.none')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_STOCK_LOCATION}>
                            {t('production.portlessManifest.stockLocation.none')}
                          </SelectItem>
                          {stockLocationOptions(manifestLocation.stockLocationId).map((l) => (
                            <SelectItem key={l.id} value={l.id}>
                              {l.isActive
                                ? l.name
                                : t('production.portlessManifest.stockLocation.inactiveOption', { name: l.name })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Unset is a legitimate state (Portless holds none of this
                      floor's stock), but it also silently means "the Portless
                      half of this floor's every Hybrid order is missing from its
                      sheet" — say so rather than leaving a quiet dropdown. Per
                      row, because one undesignated floor among several is
                      exactly the case a single section-level warning would hide.
                      amber-700 not amber-600: at text-xs the latter is ~3.2:1 on
                      white, below DESIGN.md §16's 4.5:1 body-text rule.
                      role="status" matches the sibling warnings above so the
                      appearance/disappearance is announced. */}
                  {isUnset && (
                    <p id={warningId} className="max-w-prose pt-2 text-xs text-amber-700" role="status">
                      {t('production.portlessManifest.stockLocation.unsetWarning')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Template preview */}
      <div className="rounded-md border bg-muted/40 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">{t('production.portlessManifest.templateTitle')}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('production.portlessManifest.templateDescription')}
        </p>
        <div className="overflow-x-auto">
          <div className="flex flex-wrap gap-1.5">
            {TEMPLATE_COLUMNS.map((col) => (
              <span
                key={col.key}
                className={`rounded border px-2 py-0.5 text-xs ${col.required ? 'font-semibold' : 'text-muted-foreground'}`}
              >
                {t(`production.portlessManifest.col.${col.key}`)}
                {col.required && <span className="text-destructive ml-0.5" aria-hidden="true">*</span>}
              </span>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{t('production.portlessManifest.templateRequiredNote')}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleDownload('crossdock')}
            disabled={downloading}
          >
            {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden="true" /> : null}
            {t('production.portlessManifest.downloadCrossdock')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleDownload('hybrid')}
            disabled={downloading}
          >
            {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden="true" /> : null}
            {t('production.portlessManifest.downloadHybrid')}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-1">
        {!canEdit && (
          <span className="text-sm text-muted-foreground">{t('production.portlessManifest.adminOnly')}</span>
        )}
        {canEdit && hasChanges && (
          <span className="text-sm text-amber-600">{t('production.portlessManifest.unsavedChanges')}</span>
        )}
        <Button onClick={handleSave} disabled={saveDisabled} className="gap-2">
          {updateMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-4 w-4" aria-hidden="true" />
          )}
          {t('production.portlessManifest.save')}
        </Button>
      </div>
    </div>
  );
}
